import type { PmReportRepository } from "../../repositories/operational-repositories.js";
import {
  PM_CONSUMABLE_SLOTS,
  emptyPmConsumableRef,
  formatPmCurrentType,
  formatPmDirection,
  formatPmFunctionalCheck,
  formatPmIntensityUnit,
  formatPmLaudo,
  formatPmSurfaceCondition,
  normalizePmReport,
  validatePmReportCompleteness,
  type PmDocumentModel,
  type PmReport
} from "../../domain/ndt/pm-report-domain.js";
import type { NdtDocumentCommon, NdtReportCommon, NdtValidationResult } from "../../domain/ndt/ndt-report-common.js";
import { formatIllumination } from "../../domain/ndt/lp-report-domain.js";
import { formatCivilDateBR, isLpConsumableExpired } from "../../domain/ndt/lp-consumable.js";
import { formatPmConsumableType, formatPmTechnique } from "../../domain/ndt/pm-consumable.js";
import { extractCatalogCode } from "../../domain/ndt/ndt-technical-catalog.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { NdtReportServiceBase, type NdtInspectionLookup, type NdtReportSeed } from "./ndt-report-service-base.js";

export type PmReportSeed = NdtReportSeed;

/** Relatório de Ensaio por Partículas Magnéticas — domínio e documento próprios (FORM-PM). */
export class PmReportService extends NdtReportServiceBase<PmReport, PmDocumentModel> {
  constructor(repo: PmReportRepository, identityRepo?: any, inspectionLookup?: NdtInspectionLookup) {
    super(repo, {
      documentType: "PM",
      numberPrefix: "PM",
      reportLabel: "Relatório PM",
      permissions: {
        view: PermissionCode.PmReportView,
        create: PermissionCode.PmReportCreate,
        edit: PermissionCode.PmReportEdit,
        validate: PermissionCode.PmReportValidate,
        approve: PermissionCode.PmReportApprove,
        sign: PermissionCode.PmReportSign,
        manageSignatories: PermissionCode.PmReportManageSignatories
      }
    }, identityRepo, inspectionLookup);
  }

  protected newReport(base: NdtReportCommon<PmDocumentModel>, _seed: PmReportSeed | undefined): PmReport {
    return {
      ...base,
      surfaceCondition: "",
      surfaceConditionOther: "",
      technique: "",
      magnetizationMethod: "Yoke",
      currentType: "",
      currentTypeOther: "",
      magnetizationDirection: "",
      poleSpacing: "",
      illuminationType: "",
      illuminationIntensity: "",
      illuminationUnit: "LUX",
      technicalNote: "",
      yokeAssetId: null,
      yokeDescription: "",
      yokeSerialNumber: "",
      yokeCertificate: "",
      yokeCertificateDate: "",
      yokeCertificateValidity: "",
      functionalCheck: "",
      functionalCheckNote: "",
      particles: emptyPmConsumableRef(),
      contrastPaint: emptyPmConsumableRef(),
      carrier: emptyPmConsumableRef(),
      results: []
    };
  }

  protected normalize(report: PmReport): PmReport { return normalizePmReport(report); }
  protected validateCompleteness(report: PmReport): NdtValidationResult { return validatePmReportCompleteness(report); }

  protected async composeSpecific(report: PmReport, common: NdtDocumentCommon): Promise<PmDocumentModel> {
    const consumables = PM_CONSUMABLE_SLOTS
      .map(slot => ({ slot, ref: report[slot.field] }))
      .filter(({ ref }) => ref.consumableId || ref.product.trim())
      .map(({ slot, ref }) => ({
        type: slot.type,
        typeLabel: formatPmConsumableType(slot.type),
        brand: ref.brand,
        product: ref.product,
        lot: ref.lot,
        validity: formatCivilDateBR(ref.validity),
        technique: formatPmTechnique(ref.technique),
        expired: isLpConsumableExpired(ref.validity)
      }));
    const surface = report.surfaceCondition === "OUTRA" ? (report.surfaceConditionOther.trim() || "Outra") : formatPmSurfaceCondition(report.surfaceCondition);
    const current = report.currentType === "OUTRO" ? (report.currentTypeOther.trim() || "Outro") : formatPmCurrentType(report.currentType);
    return {
      ...common,
      testData: {
        procedure: report.procedureRevision,
        referenceNorm: extractCatalogCode(report.referenceNorm),
        acceptanceCriteria: report.acceptanceCriteria,
        baseMaterial: report.baseMaterial,
        surfaceCondition: surface,
        technique: formatPmTechnique(report.technique),
        magnetizationMethod: report.magnetizationMethod,
        currentType: current,
        magnetizationDirection: formatPmDirection(report.magnetizationDirection),
        poleSpacing: report.poleSpacing ? `${report.poleSpacing} mm` : "",
        illuminationType: formatIllumination(report.illuminationType),
        illuminationIntensity: report.illuminationIntensity ? `${report.illuminationIntensity} ${formatPmIntensityUnit(report.illuminationUnit)}` : "",
        testTemperature: report.testTemperature ? `${report.testTemperature} °C` : "",
        technicalNote: report.technicalNote
      },
      equipment: {
        yoke: report.yokeDescription,
        certificate: [report.yokeCertificate, report.yokeCertificateValidity ? `validade ${formatCivilDateBR(report.yokeCertificateValidity)}` : ""].filter(Boolean).join(" / "),
        serialNumber: report.yokeSerialNumber,
        functionalCheck: [formatPmFunctionalCheck(report.functionalCheck), report.functionalCheckNote].filter(Boolean).join(" — ")
      },
      consumables,
      results: report.results.map(r => ({ item: r.item, element: r.element, location: r.location, extension: r.extension, findings: r.findings, laudo: r.laudo, laudoLabel: formatPmLaudo(r.laudo) }))
    };
  }
}
